import "./EditProfile.scss";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useRef, useState } from "react";
import { useUserContext } from "@/context/UserContext";
import {
  updateProfileInfo,
  uploadProfilePicture,
} from "@/services/EditProfileService";
import CustomSpinner from "@/components/CustomSpinner/CustomSpinner";
import { FiArrowLeft } from "react-icons/fi";

const FILE_SIZE = 2000000; // 2 MB
const SUPPORTED_FORMATS = [
  "image/png",
  "image/jpeg",
  "image/gif",
  "image/webp",
];
const CloseIcon = "../../assets/images/icons/close-icon.svg";
const PlaceHolderImage = "../../assets/images/placeholder-image.png";
const EditProfile = (props: any) => {
  const { userToken, currentUser, fetchUserData } = useUserContext();
  const [profilePicPreview, setProfilePicPreview] = useState<string>(
    currentUser?.avatar ?? "",
  );
  const [profilePic, setProfilePic] = useState<any>();
  const [loader, setLoader] = useState<boolean>(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const formikRef: any = useRef();

  const formikEmptyValues = {
    // Object with empty values to set them when clicking "Clear All"
    birthdate: "",
    displayName: "",
    email: "",
    bio: "",
    website: "",
    socialHandle: "",
    twitter: "",
    facebook: "",
    instagram: "",
    tiktok: "",
    profilePic: "",
  };
  const formik = useFormik({
    initialValues: {
      birthdate: currentUser?.birthdate,
      displayName: currentUser?.name,
      email: currentUser?.email,
      bio: currentUser?.BIO,
      website: currentUser?.social,
      socialHandle: currentUser?.handle,
      twitter: currentUser?.twitter,
      facebook: currentUser?.facebook,
      instagram: currentUser?.instagram,
      tiktok: currentUser?.tiktok,
      profilePic: currentUser?.avatar,
    },
    innerRef: formikRef,
    validationSchema: Yup.object({
      displayName: Yup.string().required("Required!"),
      email: Yup.string().email("Invalid email address").required("Required"),
      birthdate: Yup.string(),
      bio: Yup.string(),
      website: Yup.string(),
      socialHandle: Yup.string(),
      twitter: Yup.string(),
      facebook: Yup.string(),
      instagram: Yup.string(),
      tiktok: Yup.string(),
      profilePic: Yup.mixed(),
    }),
    onSubmit: async (values) => {
      setLoader(true);
      if (profilePic) {
        values.profilePic = profilePic;
        let form_data = new FormData();
        form_data.append("file", profilePic);
        await uploadProfilePicture(form_data, userToken);
      }
      const userInfo = {
        birthdate: values.birthdate,
        name: values.displayName,
        email: values.email,
        BIO: values.bio,
        social: values.website,
        facebook: values.facebook,
        twitter: values.twitter,
        instagram: values.instagram,
        tiktok: values.tiktok,
        handle: values.socialHandle,
      };
      await updateProfileInfo(userInfo, userToken).then((res) => {
        if (res && res.states !== 409) {
          setLoader(false);
          setProfilePic(undefined);
          fetchUserData();
        }
      });
    },
  });
  const backToProfile = () => {
    props.setEditProfile(false);
  };

  const handleProfilePicChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > FILE_SIZE) {
        alert("File size must be 2 MB or less");
        return;
      }
      setProfilePic(file);
      const reader = new FileReader();
      reader.onload = () => {
        setProfilePicPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    } else {
      setProfilePic("");
      setProfilePicPreview("");
    }
  };

  const handleProfilePicButtonClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  return (
    <div className="edit-profile-main-container">
      <div className="go-back-container">
        <p onClick={backToProfile}>
          <FiArrowLeft />
          BACK TO PROFILE
        </p>
      </div>
      <div className="actual-edit-container">
        <div className="page-description">
          <h3>Edit profile</h3>
          <h4 className="font-light">
            You can set preferred display name, create your profile URL and
            manage other personal settings.
          </h4>
        </div>
        <div className="picture-form-container">
          <div className="edit-profile-picture">
            {profilePicPreview ? (
              <img src={profilePicPreview} />
            ) : (
              <img src={PlaceHolderImage} />
            )}
            <div className="picture-edit-text-btn">
              <h6>Profile photo</h6>
              <h4 className="font-light">PNG, GIF, WEBP or JPEG. Max 2 MB</h4>
              <button
                type="button"
                className="primary-action-btn"
                // placeholder="Upload"
                onClick={handleProfilePicButtonClick}
              >
                Upload
              </button>
            </div>
          </div>
          <div className="edit-info-form">
            <h4>Account info</h4>
            <form onSubmit={formik.handleSubmit} ref={formikRef}>
              <div className="ep-input-group">
                <label>Display name</label>
                <input
                  className="font-light"
                  type="text"
                  placeholder="Enter display name"
                  id="displayName"
                  name="displayName"
                  onChange={formik.handleChange}
                  value={formik.values.displayName}
                />
                {formik.touched.displayName && formik.errors.displayName ? (
                  <div className="error-message">
                    {formik.errors.displayName}
                  </div>
                ) : null}
              </div>
              <div className="ep-input-group">
                <label>E-mail</label>
                <input
                  className="font-light"
                  placeholder="Enter your E-mail"
                  name="email"
                  onChange={formik.handleChange}
                  value={formik.values.email}
                />
                {formik.touched.email && formik.errors.email ? (
                  <div className="error-message">{formik.errors.email}</div>
                ) : null}
              </div>
              <div className="ep-input-group">
                <label>Birth Date</label>
                <input
                  className="font-light"
                  placeholder="Enter your E-mail"
                  name="birthdate"
                  type="date"
                  onChange={formik.handleChange}
                  value={formik.values.birthdate}
                />
                {formik.touched.birthdate && formik.errors.birthdate ? (
                  <div className="error-message">{formik.errors.birthdate}</div>
                ) : null}
              </div>
              <div className="ep-input-group">
                <label>Bio</label>
                <textarea
                  className="font-light"
                  placeholder="Describe yourself in a few words"
                  name="bio"
                  onChange={formik.handleChange}
                  value={formik.values.bio}
                />
                {formik.touched.bio && formik.errors.bio ? (
                  <div className="error-message">{formik.errors.bio}</div>
                ) : null}
              </div>
              <h4>Social</h4>
              <div className="ep-input-group">
                <label>Website</label>
                <input
                  className="font-light"
                  type="text"
                  placeholder="Enter URL"
                  name="website"
                  onChange={formik.handleChange}
                  value={formik.values.website}
                />
                {formik.touched.bio && formik.errors.bio ? (
                  <div className="error-message">{formik.errors.bio}</div>
                ) : null}
              </div>
              <div className="ep-input-group">
                <label>Social Handle</label>
                <input
                  className="font-light"
                  type="text"
                  placeholder="Enter Social Handle"
                  name="socialHandle"
                  onChange={formik.handleChange}
                  value={formik.values.socialHandle}
                />
                {formik.touched.socialHandle && formik.errors.socialHandle ? (
                  <div className="error-message">
                    {formik.errors.socialHandle}
                  </div>
                ) : null}
              </div>
              <div className="ep-input-group">
                <label>TikTok</label>
                <input
                  className="font-light"
                  type="text"
                  placeholder="Enter Tiktok"
                  name="tiktok"
                  onChange={formik.handleChange}
                  value={formik.values.tiktok}
                />
                {formik.touched.tiktok && formik.errors.tiktok ? (
                  <div className="error-message">{formik.errors.tiktok}</div>
                ) : null}
              </div>
              <div className="ep-input-group">
                <label>Twitter</label>
                <input
                  className="font-light"
                  type="text"
                  placeholder="Enter Twitter"
                  name="twitter"
                  onChange={formik.handleChange}
                  value={formik.values.twitter}
                />
                {formik.touched.twitter && formik.errors.twitter ? (
                  <div className="error-message">{formik.errors.twitter}</div>
                ) : null}
              </div>
              <div className="ep-input-group">
                <label>Instagram</label>
                <input
                  className="font-light"
                  type="text"
                  placeholder="Enter Instagram"
                  name="instagram"
                  onChange={formik.handleChange}
                  value={formik.values.instagram}
                />
                {formik.touched.instagram && formik.errors.instagram ? (
                  <div className="error-message">{formik.errors.instagram}</div>
                ) : null}
              </div>
              <div className="ep-input-group">
                <label>Facebook</label>
                <input
                  className="font-light"
                  type="text"
                  placeholder="Enter Facebook"
                  name="facebook"
                  onChange={formik.handleChange}
                  value={formik.values.facebook}
                />
                {formik.touched.facebook && formik.errors.facebook ? (
                  <div className="error-message">{formik.errors.facebook}</div>
                ) : null}
              </div>
              <div className="ep-input-group">
                <input
                  type="file"
                  name="profilePic"
                  onChange={handleProfilePicChange}
                  style={{ display: "none" }}
                  accept="image/png, image/jpeg, image/gif, image/webp"
                  ref={fileInputRef}
                />
                {formik.touched.facebook && formik.errors.facebook ? (
                  <div className="error-message">{formik.errors.facebook}</div>
                ) : null}
              </div>
              <div className="form-btns-container">
                <button className="btn linear-bg ep-submit-btn" type="submit">
                  {!loader ? (
                    "Update Profile"
                  ) : (
                    <CustomSpinner size={2} borderWidth={3} />
                  )}
                </button>
                <button
                  className="clear-all-btn"
                  type="reset"
                  onClick={() => {
                    formik.setValues(formikEmptyValues);
                    setProfilePicPreview("");
                    setProfilePic("");
                  }}
                >
                  <img src={CloseIcon} className="close-icon" />
                  Clear all
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditProfile;
